//import NotFoundData from "components/NotFoundData";
import styled from "@emotion/styled";
import { Button, Input, Row, Select, Space, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "store";
import { setLoading } from "store/slices/loadingSlice";
import NotFoundData from "components/NotFoundData";
import { Dispatch, FC, SetStateAction, useEffect, useState, useCallback } from "react";
import { GetDataOrderListData, NewOrderResponse } from "services/interface";
import CardOrders from "./CardOrders";
import { useGetBankActiveList, useGetBankAllList } from "hooks/useFactory";
import { CreditCardOutlined, TableOutlined } from "@ant-design/icons";
import OrderTableList from "components/OrderManage/OrderTabsCard/OrderTableList";
import SearchOrderForm from "./SearchOrderForm";
import { Image } from 'antd';
import noImage from "assets/images/no-image.png";
import { getDataNewOrder } from "services/ordersApi";
import BulkAcceptNewOrdersModal from "./BulkAcceptNewOrdersModal";
import { useNavigate } from "react-router-dom";

/** เปิดเป็น `true` เมื่อพร้อม ship ฟีเจอร์รับคำสั่งซื้อแบบ bulk + API batch ฝั่ง backend */
export const ENABLE_BULK_ACCEPT_NEW_ORDERS_UI = true;

const { Option } = Select;

interface OrderTabsCardProps {
  data: GetDataOrderListData | undefined;
  isLoading: boolean;
  refetch: () => void;
  setSearchInput: Dispatch<SetStateAction<string>>;
  searchInput: string;
  refetchPrepare: () => void;
  refetchCancel?: () => void;
  IsHiddenPersonInfo?: boolean;
  setDateInput: Dispatch<SetStateAction<string>>;
  dateInput: string;
  refetchCredit?: () => void;
  setOpenForm?: Dispatch<SetStateAction<boolean>>;
}

const OrderTabsCard: FC<OrderTabsCardProps> = ({
  data,
  isLoading,
  refetch,
  setSearchInput,
  searchInput,
  refetchPrepare,
  refetchCancel,
  IsHiddenPersonInfo,
  setDateInput,
  dateInput,
  refetchCredit,
  setOpenForm
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const factory_id = useAppSelector((state) => state.auth.profile?.user?.role?.ref_id);
  const userContactData = useAppSelector((state) => state.auth.contact);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [showBulkAcceptModal, setShowBulkAcceptModal] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);

  // แสดงคำเตือนเมื่อมีออเดอร์ COD ใน list ที่ออเดอร์นั้นเองยังมีหนี้ค้าง (cod_credit_has_debt)
  const hasInsufficientCodOrdersInTable = (data?.order_data ?? []).some(
    (r) => r.payment_type === "COD" && r.cod_credit_has_debt === true
  );
  const showCodCreditWarning = hasInsufficientCodOrdersInTable;

  const totalRecord = parseInt(String(data?.total_record ?? "0"), 10) || 0;

  useEffect(() => {
    if (!ENABLE_BULK_ACCEPT_NEW_ORDERS_UI) return;
    setSelectedRowKeys([]);
  }, [searchInput, dateInput]);

  const tabSelected = useAppSelector((state) => state.order.tabSelected);
  useEffect(() => {
    if (tabSelected !== "0") setSelectedRowKeys([]);
  }, [tabSelected]);

  const handleSelectAllAcrossPages = useCallback(async () => {
    const fid = userContactData?.id;
    if (!fid) {
      message.error("ไม่พบข้อมูลโรงงาน");
      return;
    }
    if (totalRecord === 0) {
      message.info("ไม่มีรายการให้เลือก");
      return;
    }
    setSelectAllLoading(true);
    try {
      const cap = Math.min(totalRecord, 5000);
      const res = await getDataNewOrder({
        status: "new_order",
        search: searchInput,
        order_date: dateInput,
        offset: 0,
        limit: cap,
        factory_id: fid,
      });
      const allOrders = res.data?.order_data ?? [];
      // เครดิต COD ไม่พอ (ออเดอร์นี้มีหนี้ค้างชำระของตัวเองที่ยังไม่เคลียร์) — ห้ามเลือกไปรับพร้อมกัน
      // เหมือน checkbox ต่อแถวในตาราง (getCheckboxProps) แต่ปุ่มนี้ set selectedRowKeys ตรงๆ ไม่ผ่าน
      // Table component เลย ต้องกรองเองตรงนี้ด้วย ไม่งั้นจะเลือกออเดอร์ที่ checkbox ควร disabled ติดมาด้วย
      // (2026-08-18: ใช้ cod_credit_has_debt แทน cod_credit_reservation_status === "insufficient")
      const selectableOrders = allOrders.filter(
        (r) => !(r.payment_type === "COD" && r.cod_credit_has_debt === true)
      );
      const skippedByInsufficientCredit = allOrders.length - selectableOrders.length;
      const ids = selectableOrders.map((r) => r.order_id);
      setSelectedRowKeys(ids);
      if (ids.length < totalRecord) {
        const insufficientNote =
          skippedByInsufficientCredit > 0 ? ` (ในนั้นข้าม ${skippedByInsufficientCredit} รายการเครดิต COD ไม่พอ)` : "";
        message.warning(`เลือกได้สูงสุด ${cap} รายการต่อครั้ง (จากทั้งหมด ${totalRecord})${insufficientNote}`);
      } else if (skippedByInsufficientCredit > 0) {
        message.warning(`ข้าม ${skippedByInsufficientCredit} รายการที่เครดิต COD ไม่พอ ไม่ได้เลือกให้`);
      }
    } catch (e) {
      console.error(e);
      message.error("โหลดรายการเพื่อเลือกทั้งหมดไม่สำเร็จ");
    } finally {
      setSelectAllLoading(false);
    }
  }, [userContactData?.id, totalRecord, searchInput, dateInput]);

  const [isTable, setIsTable] = useState<boolean>(true);
  const [tempSearchInput, setTempSearchInput] = useState<string>(searchInput);

  const { data: dataBank } = useGetBankActiveList(factory_id || '');
  const { contact } = useAppSelector((state) => state.auth);

  const childrenBank: React.ReactNode[] = [];
  dataBank?.data?.map((item) => {
    return childrenBank.push(
      <Option key={item.id} value={item.id}>
        <span style={{ fontSize: '12px' }}>
          <ImageWrapper>
            <Image src={item.image ?? noImage} alt={item.bank_name} preview={false} /> {item.bank_name} {item.account_no} {item.account_name}
          </ImageWrapper>
        </span>
      </Option>
    );
  });

  const handleSearch = () => {
    setSearchInput(tempSearchInput);
  };

  useEffect(() => {
    if (isLoading) {
      dispatch(setLoading(true));
    } else {
      dispatch(setLoading(false));
    }

  }, [dispatch, isLoading]);


  // if (!data || data.order_data.length === 0) {
  //   return <NotFoundData text="ยังไม่มีรายการคำสั่งซื้อ" />;
  // }

  const refetchData = () => {
    refetch();
  };

  return (
    <>
      <WrapperAll>
        <HeadWrapper>
          <SearchOrderForm
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            setDateInput={setDateInput}
            dateInput={dateInput}
          />
          <RightIconWrapper>
            {showCodCreditWarning && (
              <>
                <Typography.Text strong type="danger" style={{ marginRight: 10, whiteSpace: "nowrap" }}>
                  เครดิตไม่พอในการรับคำสั่งซื้อ COD
                </Typography.Text>
                <Button danger onClick={() => navigate("/manage-credit/topup")} style={{ marginRight: 10 }}>
                  เติมเครดิต
                </Button>
              </>
            )}
            {ENABLE_BULK_ACCEPT_NEW_ORDERS_UI &&
              (selectedRowKeys.length === 0 ? (
                <Button
                  type="primary"
                  loading={selectAllLoading}
                  disabled={totalRecord === 0}
                  onClick={() => void handleSelectAllAcrossPages()}
                  style={{ marginRight: 10 }}
                >
                  เลือกรับคำสั่งซื้อทั้งหมด
                </Button>
              ) : (
                <Space size={8} style={{ marginRight: 10 }}>
                  <Button type="link" onClick={() => setSelectedRowKeys([])}>
                    ล้างการเลือก
                  </Button>
                  <Button type="primary" onClick={() => setShowBulkAcceptModal(true)}>
                    รับคำสั่งซื้อทั้งหมด {selectedRowKeys.length} รายการ
                  </Button>
                </Space>
              ))}
            {setOpenForm && contact?.code === "lunganek" && (
              <Button type="primary" onClick={() => {
                setOpenForm(true);
              }}>
                เพิ่มคำสั่งซื้อ
              </Button>
            )}
          </RightIconWrapper>
        </HeadWrapper>
        <CardWrapperTable>
          <OrderTableList
            data={data}
            childrenBank={childrenBank}
            refetchData={refetchData}
            refetchPrepare={refetchPrepare}
            refetchCancel={refetchCancel}
            IsHiddenPersonInfo={IsHiddenPersonInfo}
            factory_id={userContactData?.id || ''}
            {...(ENABLE_BULK_ACCEPT_NEW_ORDERS_UI
              ? { selectedRowKeys, onSelectChange: setSelectedRowKeys }
              : {})}
          />
        </CardWrapperTable>
        {/* <Row gutter={[16, 16]} style={{ overflow: "auto", backgroundColor: "#EFEFEF" }}>
        {
          isTable ?
          <CardWrapperTable>          
            <OrderTableList 
              data={data}
              childrenBank={childrenBank}
              refetchData={refetchData}
              refetchPrepare={refetchPrepare}
              refetchCancel={refetchCancel}
            />
          </CardWrapperTable>
          :
          <>
          {data.order_data.map((item, index) => {
            return (
            <CardOrders 
              item={item} 
              childrenBank={childrenBank}
              refetchData={refetchData}
            />
            );
          })}
          </>
        }

        </Row> */}
      </WrapperAll>
      {ENABLE_BULK_ACCEPT_NEW_ORDERS_UI ? (
        <BulkAcceptNewOrdersModal
          open={showBulkAcceptModal}
          onClose={() => setShowBulkAcceptModal(false)}
          orderIds={selectedRowKeys as string[]}
          factoryId={userContactData?.id || ""}
          onSuccess={() => {
            setSelectedRowKeys([]);
            refetch();
            refetchPrepare?.();
          }}
        />
      ) : null}
    </>
  );
};

export default OrderTabsCard;

const WrapperAll = styled.div`
  padding: 10px 0px 10px 0px;
  background-color: #efefef;
`;

const HeadWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  margin-bottom: 10px;
`;
const CardWrapperTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  background-color: #ffffff;
  padding: 5px;
`;

const ImageWrapper = styled.div`
  margin-right: 8px;
  width: 20px;  
  height: 20px; 
  img {
    width: 100%;
    height: 100%;
  }
`;

const RightIconWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;